import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { Upload, Loader2, FileText, CheckCircle2, TrendingUp, TrendingDown, Eraser, Zap } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Sparkles } from "lucide-react";

// Hoisted stable style objects (avoid new refs on every render)
const PIE_TOOLTIP_CONTENT_STYLE = { background: "#fff", border: "1px solid #EBEBEA", borderRadius: 12 };
const PIE_LEGEND_STYLE = { fontSize: 11 };
// Toast copy for the extra-amortization toggle. Extracted to keep the
// caller free of nested ternaries.
function _extraToggleMsg(on, mode) {
  if (!on) return "Amortización extra desmarcada";
  if (mode === "reduce_term") return "Marcado como amortización extra · reduce plazo";
  return "Marcado como amortización extra · reduce cuota";
}


const CATEGORY_LABELS = {
  nomina: "Nómina",
  hipoteca: "Hipoteca",
  alquiler: "Alquiler",
  servicios: "Servicios",
  supermercado: "Supermercado",
  restaurantes: "Restaurantes",
  transporte: "Transporte",
  gasolina: "Gasolina",
  salud: "Salud",
  ocio: "Ocio",
  viajes: "Viajes",
  compras: "Compras",
  educacion: "Educación",
  impuestos: "Impuestos",
  transferencias: "Transferencias",
  ingresos: "Otros ingresos",
  cripto: "Cripto",
  inversiones: "Inversiones",
  otros: "Otros",
};

const CAT_COLORS = [
  "#0F291E", "#1C7C54", "#4B9471", "#8EBFA6", "#D1E2DA",
  "#D95D39", "#E58B6E", "#B8CFC4", "#3C6A55", "#75A891",
  "#2E5A45", "#A8C4B6", "#6B8E80", "#4A7A63", "#89AA9A",
  "#5B8574", "#93B5A5", "#6E9682", "#B0C7BB",
];

export function ImportStatementDialog({ open, onOpenChange, account, onImported }) {
  const [file, setFile] = useState(null);
  const [updateBalance, setUpdateBalance] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setFile(null); setResult(null); setUpdateBalance(true); }
  }, [open]);

  const submit = async () => {
    if (!file) return toast.error("Selecciona un archivo");
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("update_balance", updateBalance ? "true" : "false");
      const res = await api.post(`/accounts/${account.id}/import-statement`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });

      // Long statements return a job_id; poll until done so we don't hit the
      // ingress HTTP timeout on the initial POST.
      if (res.data?.job_id) {
        const jobId = res.data.job_id;
        let finished = null;
        // Poll every 3s for up to 8 minutes (160 attempts). Enough for ~700+ tx.
        for (let i = 0; i < 160; i += 1) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const s = await api.get(`/import-jobs/${jobId}`);
            if (s.data?.status === "done") { finished = s.data; break; }
            if (s.data?.status === "error") {
              throw new Error(s.data.error || "Error importando el extracto");
            }
          } catch (e) {
            // Transient network error — keep polling
            if (i > 3 && !e.response) continue;
            if (e.response?.status === 404) throw new Error("Trabajo no encontrado");
          }
        }
        if (!finished) throw new Error("El importador tardó demasiado. Prueba con un extracto más corto.");
        setResult(finished);
        const dupMsg = finished.duplicates > 0
          ? ` · ${finished.duplicates} duplicado${finished.duplicates === 1 ? "" : "s"} omitido${finished.duplicates === 1 ? "" : "s"}`
          : "";
        toast.success(`Importadas ${finished.imported} transacciones${dupMsg}`);
        onImported?.();
      } else {
        setResult(res.data);
        const dupMsg = res.data.duplicates > 0
          ? ` · ${res.data.duplicates} duplicado${res.data.duplicates === 1 ? "" : "s"} omitido${res.data.duplicates === 1 ? "" : "s"}`
          : "";
        toast.success(`Importadas ${res.data.imported} transacciones${dupMsg}`);
        onImported?.();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "No se pudo importar";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Importar extracto</DialogTitle>
          <p className="text-sm text-[#5F7368]">
            Cuenta: <b>{account.bank}</b> · {account.alias}
          </p>
        </DialogHeader>

        {!result ? (
          <div className="flex flex-col gap-4 py-2">
            <label
              htmlFor="statement-file"
              data-testid="statement-file-label"
              className="border-2 border-dashed border-[#EBEBEA] rounded-xl px-6 py-10 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#1C7C54]/40 transition-colors"
            >
              {file ? (
                <>
                  <FileText className="w-8 h-8 text-[#1C7C54]" />
                  <div className="text-sm font-medium text-[#0A110E]">{file.name}</div>
                  <div className="text-xs text-[#8A9992]">{(file.size / 1024).toFixed(1)} KB · Click para cambiar</div>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-[#8A9992]" />
                  <div className="text-sm font-medium text-[#0A110E]">Arrastra o selecciona un PDF o CSV</div>
                  <div className="text-xs text-[#8A9992]">Máx. 8 MB · La IA extraerá y categorizará los movimientos</div>
                </>
              )}
              <input
                id="statement-file"
                data-testid="statement-file-input"
                type="file"
                accept=".pdf,.csv,text/csv,application/pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>

            <label className="flex items-center gap-2 text-sm text-[#5F7368]">
              <input
                type="checkbox"
                data-testid="update-balance-check"
                checked={updateBalance}
                onChange={(e) => setUpdateBalance(e.target.checked)}
                className="w-4 h-4 accent-[#1C7C54]"
              />
              Actualizar el saldo con el saldo final del extracto
            </label>

            {uploading && (
              <div className="text-sm text-[#5F7368] flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Analizando extracto con IA… puede tardar 15-40s
              </div>
            )}
          </div>
        ) : (
          <div className="py-2 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-[#1C7C54]">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Extracto procesado</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-[#EBEBEA] px-3 py-2">
                <div className="text-xs text-[#8A9992]">Transacciones</div>
                <div className="font-display text-xl tabular-nums font-semibold">{result.imported}</div>
              </div>
              <div className="rounded-lg border border-[#EBEBEA] px-3 py-2">
                <div className="text-xs text-[#8A9992]">Saldo final detectado</div>
                <div className="font-display text-xl tabular-nums font-semibold">
                  {result.final_balance != null ? formatCurrency(result.final_balance, result.detected_currency || account.currency) : "—"}
                </div>
              </div>
            </div>
            {result.duplicates > 0 && (
              <div
                data-testid="import-duplicates-note"
                className="text-xs text-[#5F7368] bg-[#FAFAF9] border border-[#EBEBEA] rounded-lg px-3 py-2"
              >
                Omitimos {result.duplicates} movimiento{result.duplicates === 1 ? "" : "s"} ya presente{result.duplicates === 1 ? "" : "s"} en la cuenta para evitar duplicados.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            {result ? "Cerrar" : "Cancelar"}
          </Button>
          {!result && (
            <Button
              data-testid="import-submit-btn"
              onClick={submit}
              disabled={!file || uploading}
              className="bg-[#0F291E] hover:bg-[#1C7C54]"
            >
              {uploading ? "Analizando…" : "Importar"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReimportBalancesDialog({ open, onOpenChange, account, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setFile(null); setResult(null); }
  }, [open]);

  const submit = async () => {
    if (!file) return toast.error("Selecciona un archivo CSV");
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/accounts/${account.id}/reimport-balances`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setResult(res.data);
      const suffix = res.data.unmatched_count > 0
        ? ` · ${res.data.unmatched_count} sin match`
        : "";
      toast.success(`${res.data.updated} saldo${res.data.updated === 1 ? "" : "s"} actualizado${res.data.updated === 1 ? "" : "s"}${suffix}`);
      onImported?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "No se pudo reimportar";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const downloadUnmatched = () => {
    if (!result?.unmatched?.length) return;
    const header = "Fecha,Descripción,Importe,Saldo,Motivo";
    const escape = (s) => {
      const v = String(s ?? "");
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };
    const rows = result.unmatched.map((r) => [
      r.date, r.description || "", r.amount, r.balance_after, r.reason,
    ].map(escape).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `saldos-sin-match-${account.alias || account.id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Reimportar saldos</DialogTitle>
          <p className="text-sm text-[#5F7368]">
            Cuenta: <b>{account.bank}</b> · {account.alias}
          </p>
        </DialogHeader>

        {!result ? (
          <div className="flex flex-col gap-4 py-2">
            <div className="text-xs text-[#5F7368] bg-[#FAFAF9] border border-[#EBEBEA] rounded-lg px-3 py-2">
              Sube un CSV para actualizar únicamente el saldo (columna <b>Saldo</b>) de las transacciones ya existentes.
              No se crean ni eliminan movimientos. Emparejamos por <b>fecha + importe + moneda</b> y usamos la descripción para desempatar.
            </div>
            <label
              htmlFor="reimport-file"
              data-testid="reimport-file-label"
              className="border-2 border-dashed border-[#EBEBEA] rounded-xl px-6 py-10 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#1C7C54]/40 transition-colors"
            >
              {file ? (
                <>
                  <FileText className="w-8 h-8 text-[#1C7C54]" />
                  <div className="text-sm font-medium text-[#0A110E]">{file.name}</div>
                  <div className="text-xs text-[#8A9992]">{(file.size / 1024).toFixed(1)} KB · Click para cambiar</div>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-[#8A9992]" />
                  <div className="text-sm font-medium text-[#0A110E]">Selecciona un CSV con columna Saldo</div>
                  <div className="text-xs text-[#8A9992]">Máx. 8 MB · Solo actualiza saldos, no importa movimientos</div>
                </>
              )}
              <input
                id="reimport-file"
                data-testid="reimport-file-input"
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            {uploading && (
              <div className="text-sm text-[#5F7368] flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Analizando y emparejando saldos…
              </div>
            )}
          </div>
        ) : (
          <div className="py-2 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-[#1C7C54]">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Reimportación completada</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg border border-[#EBEBEA] px-3 py-2">
                <div className="text-xs text-[#8A9992]">Filas leídas</div>
                <div className="font-display text-xl tabular-nums font-semibold" data-testid="reimport-total">{result.total_rows}</div>
              </div>
              <div className="rounded-lg border border-[#EBEBEA] px-3 py-2">
                <div className="text-xs text-[#8A9992]">Actualizadas</div>
                <div className="font-display text-xl tabular-nums font-semibold text-[#1C7C54]" data-testid="reimport-updated">{result.updated}</div>
              </div>
              <div className="rounded-lg border border-[#EBEBEA] px-3 py-2">
                <div className="text-xs text-[#8A9992]">Sin match</div>
                <div className="font-display text-xl tabular-nums font-semibold text-[#D95D39]" data-testid="reimport-unmatched">{result.unmatched_count}</div>
              </div>
            </div>
            {result.unchanged > 0 && (
              <div className="text-xs text-[#5F7368] bg-[#FAFAF9] border border-[#EBEBEA] rounded-lg px-3 py-2">
                {result.unchanged} movimiento{result.unchanged === 1 ? "" : "s"} ya tenía{result.unchanged === 1 ? "" : "n"} el saldo correcto y no se modificó{result.unchanged === 1 ? "" : "eron"}.
              </div>
            )}
            {result.unmatched_count > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-xs text-[#5F7368]">
                  {result.unmatched_count} fila{result.unmatched_count === 1 ? "" : "s"} del CSV no se pudo{result.unmatched_count === 1 ? "" : "eron"} emparejar con transacciones existentes.
                </div>
                <Button
                  variant="outline"
                  onClick={downloadUnmatched}
                  data-testid="reimport-download-unmatched"
                  className="w-fit"
                >
                  Descargar sin match (.csv)
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            {result ? "Cerrar" : "Cancelar"}
          </Button>
          {!result && (
            <Button
              data-testid="reimport-submit-btn"
              onClick={submit}
              disabled={!file || uploading}
              className="bg-[#0F291E] hover:bg-[#1C7C54]"
            >
              {uploading ? "Procesando…" : "Reimportar saldos"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TransactionsDialog({ open, onOpenChange, account, refreshKey }) {
  const [txs, setTxs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [debts, setDebts] = useState([]);
  const [dedupRunning, setDedupRunning] = useState(false);
  // Extra-amortization mode picker: when set, opens an alert dialog with
  // the "reduce term / reduce payment" choice + a live simulation preview.
  const [extraDialog, setExtraDialog] = useState(null);  // {tx, mode, sim, loading}
  // Filters
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all"); // "all" | "none" | property id

  const loadAll = () => {
    if (!account) return;
    setLoading(true);
    Promise.all([
      api.get(`/accounts/${account.id}/transactions`),
      api.get(`/transactions/summary?account_id=${account.id}`),
      api.get(`/investments`),
      api.get(`/debts`).catch(() => ({ data: [] })),
    ])
      .then(([a, b, c, d]) => {
        setTxs(a.data);
        setSummary(b.data);
        const allInv = c.data || [];
        setProperties(allInv.filter((i) => i.type === "real_estate"));
        setInvestments(allInv.filter((i) => i.type !== "real_estate"));
        // Only expose mortgages (or debts flagged as such) for the picker —
        // linking a random credit-card debt to a bank tx isn't meaningful.
        setDebts((d.data || []).filter((x) => x.is_mortgage || /hipotec|mortgage/i.test(x.description || "")));
      })
      .catch((e) => logger.error("tx load", e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !account) return;
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account, refreshKey]);

  const runDedup = async () => {
    setDedupRunning(true);
    try {
      const r = await api.post(`/accounts/${account.id}/deduplicate`);
      const del = r.data?.deleted || 0;
      if (del === 0) {
        toast.info("No se encontraron duplicados");
      } else {
        toast.success(`Eliminados ${del} movimientos duplicados`);
        loadAll();
      }
    } catch (e) {
      logger.error("dedup", e);
      toast.error(e?.response?.data?.detail || "No se pudo limpiar duplicados");
    } finally {
      setDedupRunning(false);
    }
  };

  const updateCategory = async (tx, newCat) => {
    if (newCat === tx.category) return;
    setSavingId(tx.id);
    // optimistic update
    setTxs((prev) => prev.map((t) => (t.id === tx.id ? { ...t, category: newCat } : t)));
    try {
      await api.patch(`/transactions/${tx.id}`, { category: newCat });
      toast.success("Categoría actualizada · aprendiendo…", { icon: "✨" });
      // reload summary silently
      const s = await api.get(`/transactions/summary?account_id=${account.id}`);
      setSummary(s.data);
      // Auto-suggest a link when the user tags a tx as "inversiones" and no
      // investment is linked yet. We ping the suggestions endpoint and, if the
      // top hit is confident (hint or ≥60 amount match), offer a one-click
      // link via a toast action.
      if (newCat === "inversiones" && !tx.linked_investment_id && !tx.linked_property_id) {
        try {
          const sr = await api.get(`/transactions/${tx.id}/link-suggestions`);
          const top = (sr.data?.suggestions || [])[0];
          if (top && top.score >= 60) {
            toast(`¿Vincular a ${top.name}?`, {
              description: top.score === 100
                ? "Ya vinculaste algo parecido antes."
                : `El importe coincide con el coste de esta ${top.type}.`,
              duration: 10000,
              action: {
                label: "Vincular",
                onClick: () => updateInvestment({ ...tx, category: newCat }, top.id),
              },
            });
          }
        } catch (e) { logger.warn("suggest failed", e); }
      }
    } catch (e) {
      toast.error("No se pudo actualizar");
      // rollback
      setTxs((prev) => prev.map((t) => (t.id === tx.id ? { ...t, category: tx.category } : t)));
    } finally {
      setSavingId(null);
    }
  };

  const updateProperty = async (tx, propertyId) => {
    // Optimistic update
    setTxs((prev) => prev.map((t) => (t.id === tx.id ? { ...t, linked_property_id: propertyId || null } : t)));
    try {
      const r = await api.patch(`/transactions/${tx.id}`, { linked_property_id: propertyId });
      const similar = r.data?.similar_unlinked || 0;
      if (propertyId && similar > 0) {
        // Ask user if they want to bulk-link the sibling txs. We do this via a
        // toast with an action button so it's a single, gentle interaction.
        const propName = properties.find((p) => p.id === propertyId)?.name || "el inmueble";
        toast(`Encontramos ${similar} mov. similar${similar === 1 ? "" : "es"}`, {
          description: `¿Vincularlos también a ${propName}?`,
          duration: 8000,
          action: {
            label: "Vincular todos",
            onClick: async () => {
              try {
                const br = await api.post("/transactions/bulk-link-similar", {
                  source_tx_id: tx.id,
                  property_id: propertyId,
                });
                const n = br.data?.updated || 0;
                if (n > 0) {
                  toast.success(`🏠 ${n} movimiento${n === 1 ? "" : "s"} vinculado${n === 1 ? "" : "s"}`);
                  // Refresh the tx list to reflect the new linked state
                  loadAll();
                }
              } catch (err) {
                logger.warn("bulk link failed", err);
                toast.error("No se pudo aplicar en lote");
              }
            },
          },
        });
      } else {
        toast.success(propertyId ? "🏠 Vinculado a inmueble" : "Vínculo eliminado");
      }
    } catch (e) {
      logger.warn("link property failed", e);
      toast.error("No se pudo vincular");
      // rollback
      setTxs((prev) => prev.map((t) => (t.id === tx.id ? { ...t, linked_property_id: tx.linked_property_id || null } : t)));
    }
  };

  const updateInvestment = async (tx, investmentId) => {
    // Optimistic
    setTxs((prev) => prev.map((t) =>
      t.id === tx.id ? { ...t, linked_investment_id: investmentId || null } : t,
    ));
    try {
      await api.patch(`/transactions/${tx.id}`, { linked_investment_id: investmentId });
      const invName = investmentId ? (investments.find((i) => i.id === investmentId)?.name || "la inversión") : null;
      toast.success(investmentId ? `Vinculado a ${invName}` : "Vínculo eliminado");
    } catch (e) {
      logger.warn("link investment failed", e);
      toast.error(e?.response?.data?.detail || "No se pudo vincular");
      // rollback
      setTxs((prev) => prev.map((t) =>
        t.id === tx.id ? { ...t, linked_investment_id: tx.linked_investment_id || null } : t,
      ));
    }
  };

  const updateDebt = async (tx, debtId) => {
    setTxs((prev) => prev.map((t) =>
      t.id === tx.id ? { ...t, linked_debt_id: debtId || null } : t,
    ));
    try {
      await api.patch(`/transactions/${tx.id}`, { linked_debt_id: debtId });
      const debtName = debtId ? (debts.find((d) => d.id === debtId)?.creditor || "la hipoteca") : null;
      toast.success(debtId ? `Amortizado en ${debtName}` : "Vínculo eliminado");
    } catch (e) {
      logger.warn("link debt failed", e);
      toast.error(e?.response?.data?.detail || "No se pudo vincular");
      setTxs((prev) => prev.map((t) =>
        t.id === tx.id ? { ...t, linked_debt_id: tx.linked_debt_id || null } : t,
      ));
    }
  };

  const toggleExtraAmort = async (tx, mode) => {
    // ``mode`` = null → turn OFF, otherwise "reduce_term" | "reduce_payment"
    const on = Boolean(mode);
    setTxs((prev) => prev.map((t) =>
      t.id === tx.id ? {
        ...t,
        is_extra_amortization: on,
        extra_amort_mode: on ? mode : null,
      } : t,
    ));
    try {
      const payload = on
        ? { is_extra_amortization: true, extra_amort_mode: mode }
        : { is_extra_amortization: false, extra_amort_mode: "" };
      await api.patch(`/transactions/${tx.id}`, payload);
      toast.success(_extraToggleMsg(on, mode));
    } catch (e) {
      logger.warn("extra amort toggle failed", e);
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
      // rollback
      setTxs((prev) => prev.map((t) =>
        t.id === tx.id ? {
          ...t,
          is_extra_amortization: tx.is_extra_amortization || false,
          extra_amort_mode: tx.extra_amort_mode || null,
        } : t,
      ));
    }
  };

  const openExtraDialog = async (tx, mode) => {
    setExtraDialog({ tx, mode, sim: null, loading: true });
    try {
      const { data } = await api.post(
        `/debts/${tx.linked_debt_id}/extra-amortization-simulate`,
        { tx_id: tx.id, mode },
      );
      setExtraDialog((s) => (s ? { ...s, sim: data, loading: false } : s));
    } catch (e) {
      logger.warn("simulate extra amort", e);
      setExtraDialog((s) => (s ? { ...s, sim: null, loading: false } : s));
    }
  };

  const changeExtraDialogMode = async (mode) => {
    setExtraDialog((s) => (s ? { ...s, mode, sim: null, loading: true } : s));
    try {
      const target = extraDialog?.tx;
      if (!target) return;
      const { data } = await api.post(
        `/debts/${target.linked_debt_id}/extra-amortization-simulate`,
        { tx_id: target.id, mode },
      );
      setExtraDialog((s) => (s ? { ...s, sim: data, loading: false } : s));
    } catch (e) {
      logger.warn("simulate extra amort mode change", e);
      setExtraDialog((s) => (s ? { ...s, sim: null, loading: false } : s));
    }
  };

  const confirmExtraAmort = async () => {
    if (!extraDialog?.tx || !extraDialog?.mode) return;
    await toggleExtraAmort(extraDialog.tx, extraDialog.mode);
    setExtraDialog(null);
  };

  // Build the filtered tx list for the current selection
  const availableCategories = React.useMemo(() => {
    const set = new Set();
    txs.forEach((t) => t.category && set.add(t.category));
    return Array.from(set).sort();
  }, [txs]);

  const filteredTxs = React.useMemo(() => (
    txs.filter((t) => {
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      if (propertyFilter === "none" && t.linked_property_id) return false;
      if (propertyFilter !== "all" && propertyFilter !== "none" && t.linked_property_id !== propertyFilter) return false;
      return true;
    })
  ), [txs, categoryFilter, propertyFilter]);

  // Running balance after each transaction, keyed by transaction id.
  //
  // We prefer `balance_after` from the bank statement itself (populated at
  // import time when the CSV contained a "Saldo"/"Balance" column) so the
  // number matches your paper statement exactly. When the field isn't
  // available, we fall back to the account's current balance as the anchor
  // for the newest transaction and walk backwards:
  //   balance_after[N] = balance_after[N+1] - tx[N+1].amount
  // Filters don't affect the calculation — the running balance always reflects
  // the real historical balance so it stays coherent when the user filters.
  const runningBalanceById = React.useMemo(() => {
    if (!account || !txs.length) return {};
    const ordered = [...txs].sort((a, b) => {
      const dateCmp = (b.date || "").localeCompare(a.date || "");
      if (dateCmp !== 0) return dateCmp;
      return (b.id || "").localeCompare(a.id || "");
    });
    const map = {};
    let running = Number(account.balance) || 0;
    for (const t of ordered) {
      if (t.balance_after != null) {
        // Trust the imported bank balance whenever it exists.
        map[t.id] = Number(t.balance_after);
        running = Number(t.balance_after) - (Number(t.amount) || 0);
      } else {
        map[t.id] = running;
        running -= Number(t.amount) || 0;
      }
    }
    return map;
  }, [txs, account]);

  const pieData = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.by_category || {})
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ name: CATEGORY_LABELS[k] || k, value: v }))
      .sort((a, b) => b.value - a.value);
  }, [summary]);

  if (!account) return null;
  const currency = txs[0]?.currency || account.currency || "USD";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl bg-white max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <DialogTitle className="font-display text-2xl">
              Movimientos — {account.bank} · {account.alias}
            </DialogTitle>
            {txs.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={dedupRunning}
                    data-testid="dedup-btn"
                    className="rounded-lg border-[#EBEBEA] text-[#5F7368] hover:bg-[#FAFAF9]"
                  >
                    <Eraser className={`w-3.5 h-3.5 mr-1.5 ${dedupRunning ? "animate-pulse" : ""}`} />
                    {dedupRunning ? "Limpiando…" : "Limpiar duplicados"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Limpiar duplicados?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Se buscarán movimientos con la misma descripción, importe y moneda en ±5 días.
                      Se conservará el más antiguo de cada grupo y se eliminarán los demás. La acción no se puede deshacer.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="dedup-confirm-btn"
                      onClick={runDedup}
                      className="bg-[#D95D39] hover:bg-[#D95D39]/90"
                    >
                      Limpiar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </DialogHeader>

        {(() => {
          if (loading) {
            return (
              <div className="p-8 text-[#5F7368] text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
              </div>
            );
          }
          if (txs.length === 0) {
            return (
              <div className="p-8 text-[#5F7368] text-sm">
                Aún no hay movimientos importados en esta cuenta.
              </div>
            );
          }
          return (
          <div className="flex-1 overflow-hidden flex flex-col gap-6">
            {/* Totals */}
            <div className="grid grid-cols-3 gap-4">
              <MiniStat icon={TrendingUp} label="Ingresos" value={formatCurrency(summary?.income || 0, currency)} tone="text-[#1C7C54]" />
              <MiniStat icon={TrendingDown} label="Gastos" value={formatCurrency(summary?.expense || 0, currency)} tone="text-[#D95D39]" />
              <MiniStat label="Neto" value={formatCurrency(summary?.net || 0, currency)} tone={(summary?.net || 0) >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 flex-1 overflow-hidden">
              {/* Pie */}
              <div className="md:col-span-2 rounded-xl border border-[#EBEBEA] p-4 flex flex-col">
                <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] mb-2">Gastos por categoría</div>
                <div className="flex-1 min-h-[240px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        innerRadius={45}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((d, i) => (
                          <Cell key={d.name} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v) => formatCurrency(v, currency)}
                        contentStyle={PIE_TOOLTIP_CONTENT_STYLE}
                      />
                      <Legend wrapperStyle={PIE_LEGEND_STYLE} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Table */}
              <div className="md:col-span-3 rounded-xl border border-[#EBEBEA] overflow-auto">
                {/* Filters */}
                <div
                  data-testid="tx-filters"
                  className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-[#EBEBEA] bg-[#FAFAF9]"
                >
                  <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Filtrar</span>
                  <select
                    data-testid="tx-filter-category"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="h-7 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs focus:outline-none focus:border-[#1C7C54]/60"
                  >
                    <option value="all">Todas las categorías</option>
                    {availableCategories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <select
                    data-testid="tx-filter-property"
                    value={propertyFilter}
                    onChange={(e) => setPropertyFilter(e.target.value)}
                    className="h-7 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs focus:outline-none focus:border-[#1C7C54]/60"
                  >
                    <option value="all">Todos los inmuebles</option>
                    <option value="none">Sin vincular</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <span className="ml-auto text-[11px] text-[#5F7368]">
                    {filteredTxs.length} de {txs.length} mov
                  </span>
                </div>
                <table className="w-full text-sm" data-testid="transactions-table">
                  <thead className="bg-[#FAFAF9] sticky top-0 z-10">
                    <tr className="text-xs uppercase tracking-[0.08em] text-[#8A9992]">
                      <th className="text-left px-3 py-2">Fecha</th>
                      <th className="text-left px-3 py-2">Concepto</th>
                      <th className="text-left px-3 py-2">Categoría</th>
                      <th className="text-left px-3 py-2">Inmueble</th>
                      <th className="text-right px-3 py-2">Importe</th>
                      <th className="text-right px-3 py-2">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTxs.map((t) => (
                      <tr key={t.id} className="border-t border-[#EBEBEA]">
                        <td className="px-3 py-2 text-[#5F7368] tabular-nums whitespace-nowrap">{t.date}</td>
                        <td className="px-3 py-2 text-[#0A110E] truncate max-w-[140px]" title={t.description}>{t.description}</td>
                        <td className="px-3 py-2">
                          <Select
                            value={t.category}
                            onValueChange={(v) => updateCategory(t, v)}
                            disabled={savingId === t.id}
                          >
                            <SelectTrigger
                              data-testid={`cat-select-${t.id}`}
                              className="h-7 px-2 py-0 text-[10px] uppercase tracking-wider rounded-full bg-[#1C7C54]/10 border-0 text-[#1C7C54] hover:bg-[#1C7C54]/15 focus:ring-0 focus:ring-offset-0 w-auto min-w-0 gap-1"
                            >
                              <span className="flex items-center gap-1">
                                {savingId === t.id && <Sparkles className="w-3 h-3 animate-pulse" />}
                                {CATEGORY_LABELS[t.category] || t.category}
                              </span>
                            </SelectTrigger>
                            <SelectContent align="start">
                              {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                                <SelectItem key={k} value={k} data-testid={`cat-option-${k}`}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">
                          {(() => {
                            const investmentGroups = {
                              deposit: investments.filter((i) => i.type === "deposit"),
                              stock: investments.filter((i) => i.type === "stock"),
                              crypto: investments.filter((i) => i.type === "crypto"),
                              fund: investments.filter((i) => i.type === "fund"),
                              other: investments.filter((i) => i.type === "other"),
                            };
                            const totalOptions = properties.length + investments.length + debts.length;
                            if (totalOptions === 0) return <span className="text-[10px] text-[#8A9992]">—</span>;

                            // Current selection is a prefixed key so the same
                            // dropdown handles property / investment / debt links.
                            let currentValue = "";
                            if (t.linked_property_id) currentValue = `p:${t.linked_property_id}`;
                            else if (t.linked_investment_id) currentValue = `i:${t.linked_investment_id}`;
                            else if (t.linked_debt_id) currentValue = `d:${t.linked_debt_id}`;

                            const handle = (raw) => {
                              // Clear all sides then set the chosen one.
                              if (!raw) {
                                if (t.linked_property_id) updateProperty(t, "");
                                if (t.linked_investment_id) updateInvestment(t, "");
                                if (t.linked_debt_id) updateDebt(t, "");
                                return;
                              }
                              const [kind, id] = raw.split(":");
                              if (kind === "p") {
                                if (t.linked_investment_id) updateInvestment(t, "");
                                if (t.linked_debt_id) updateDebt(t, "");
                                updateProperty(t, id);
                              } else if (kind === "i") {
                                if (t.linked_property_id) updateProperty(t, "");
                                if (t.linked_debt_id) updateDebt(t, "");
                                updateInvestment(t, id);
                              } else {
                                if (t.linked_property_id) updateProperty(t, "");
                                if (t.linked_investment_id) updateInvestment(t, "");
                                updateDebt(t, id);
                              }
                            };

                            const linkedLabel = () => {
                              if (t.linked_property_id) {
                                return properties.find((p) => p.id === t.linked_property_id)?.name || null;
                              }
                              if (t.linked_investment_id) {
                                return investments.find((i) => i.id === t.linked_investment_id)?.name || null;
                              }
                              if (t.linked_debt_id) {
                                return debts.find((d) => d.id === t.linked_debt_id)?.creditor || null;
                              }
                              return null;
                            };
                            const label = linkedLabel();

                            return (
                              <select
                                data-testid={`link-tx-${t.id}`}
                                value={currentValue}
                                onChange={(e) => handle(e.target.value)}
                                title={label || "Vincular a un inmueble o inversión"}
                                className={`h-7 text-[10px] rounded-md border bg-white focus:outline-none focus:border-[#1C7C54]/60 max-w-[140px] ${
                                  currentValue
                                    ? "border-[#1C7C54]/40 text-[#1C7C54] font-semibold"
                                    : "border-[#EBEBEA] text-[#8A9992]"
                                }`}
                              >
                                <option value="">Vincular…</option>
                                {properties.length > 0 && (
                                  <optgroup label="🏠 Inmuebles">
                                    {properties.map((p) => (
                                      <option key={p.id} value={`p:${p.id}`}>{p.name}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {debts.length > 0 && (
                                  <optgroup label="🏦 Hipotecas">
                                    {debts.map((dd) => (
                                      <option key={dd.id} value={`d:${dd.id}`}>{dd.creditor}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {investmentGroups.deposit.length > 0 && (
                                  <optgroup label="🏦 Depósitos">
                                    {investmentGroups.deposit.map((i) => (
                                      <option key={i.id} value={`i:${i.id}`}>{i.name}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {investmentGroups.stock.length > 0 && (
                                  <optgroup label="📈 Acciones / ETF">
                                    {investmentGroups.stock.map((i) => (
                                      <option key={i.id} value={`i:${i.id}`}>{i.name}{i.ticker ? ` (${i.ticker})` : ""}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {investmentGroups.fund.length > 0 && (
                                  <optgroup label="📊 Fondos">
                                    {investmentGroups.fund.map((i) => (
                                      <option key={i.id} value={`i:${i.id}`}>{i.name}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {investmentGroups.crypto.length > 0 && (
                                  <optgroup label="🪙 Cripto">
                                    {investmentGroups.crypto.map((i) => (
                                      <option key={i.id} value={`i:${i.id}`}>{i.name}{i.ticker ? ` (${i.ticker})` : ""}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {investmentGroups.other.length > 0 && (
                                  <optgroup label="🧾 Otros">
                                    {investmentGroups.other.map((i) => (
                                      <option key={i.id} value={`i:${i.id}`}>{i.name}</option>
                                    ))}
                                  </optgroup>
                                )}
                              </select>
                            );
                          })()}
                          {t.linked_debt_id && t.amount < 0 && (
                            <button
                              type="button"
                              data-testid={`extra-amort-${t.id}`}
                              onClick={() => {
                                if (t.is_extra_amortization) {
                                  // Already extra → untoggle silently
                                  toggleExtraAmort(t, null);
                                } else {
                                  openExtraDialog(t, "reduce_term");
                                }
                              }}
                              title={t.is_extra_amortization
                                ? "Quitar amortización extra"
                                : "Marcar como amortización extra"}
                              className={`ml-1 inline-flex items-center justify-center w-6 h-6 rounded-md border transition-colors ${
                                t.is_extra_amortization
                                  ? "bg-[#1C7C54]/10 border-[#1C7C54]/40 text-[#1C7C54]"
                                  : "bg-white border-[#EBEBEA] text-[#8A9992] hover:text-[#1C7C54] hover:border-[#1C7C54]/40"
                              }`}
                            >
                              <Zap className="w-3 h-3" />
                            </button>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${t.amount >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
                          {formatCurrency(t.amount, t.currency || currency)}
                        </td>
                        <td
                          className="px-3 py-2 text-right tabular-nums text-[#0A110E] whitespace-nowrap"
                          data-testid={`balance-${t.id}`}
                        >
                          {runningBalanceById[t.id] !== undefined
                            ? formatCurrency(runningBalanceById[t.id], t.currency || currency)
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          );
        })()}
      </DialogContent>

      {/* Extra amortization confirmation dialog with live simulation */}
      <AlertDialog open={!!extraDialog} onOpenChange={(v) => !v && setExtraDialog(null)}>
        <AlertDialogContent className="bg-white sm:max-w-lg" data-testid="extra-amort-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">Marcar como amortización extra</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5F7368]">
              Este movimiento se aplicará <b>íntegro a capital</b> (sin repartir con intereses)
              y reducirá el saldo pendiente de la hipoteca.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid grid-cols-2 gap-2 my-4">
            <button
              type="button"
              data-testid="extra-mode-reduce-term"
              onClick={() => changeExtraDialogMode("reduce_term")}
              className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                extraDialog?.mode === "reduce_term"
                  ? "border-[#1C7C54] bg-[#1C7C54]/5"
                  : "border-[#EBEBEA] hover:border-[#1C7C54]/40"
              }`}
            >
              <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Reduce plazo</div>
              <div className="text-sm font-medium mt-0.5 text-[#0A110E]">Cuota igual · menos meses</div>
            </button>
            <button
              type="button"
              data-testid="extra-mode-reduce-payment"
              onClick={() => changeExtraDialogMode("reduce_payment")}
              className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                extraDialog?.mode === "reduce_payment"
                  ? "border-[#1C7C54] bg-[#1C7C54]/5"
                  : "border-[#EBEBEA] hover:border-[#1C7C54]/40"
              }`}
            >
              <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Reduce cuota</div>
              <div className="text-sm font-medium mt-0.5 text-[#0A110E]">Meses igual · cuota más baja</div>
            </button>
          </div>

          {extraDialog?.loading && (
            <div className="text-xs text-[#8A9992]" data-testid="extra-sim-loading">Calculando impacto…</div>
          )}
          {extraDialog?.sim && (
            <div
              className="rounded-lg bg-[#FAFAF9] border border-[#EBEBEA] p-3 grid grid-cols-2 gap-3 text-sm"
              data-testid="extra-sim-panel"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Saldo tras la amortización</div>
                <div className="font-display font-semibold tabular-nums mt-0.5">
                  {formatCurrency(extraDialog.sim.balance_after, extraDialog.sim.currency)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">
                  {extraDialog.mode === "reduce_term" ? "Meses ahorrados" : "Nueva cuota"}
                </div>
                <div className="font-display font-semibold tabular-nums mt-0.5 text-[#1C7C54]">
                  {extraDialog.mode === "reduce_term"
                    ? `${extraDialog.sim.months_saved} m.`
                    : formatCurrency(extraDialog.sim.monthly_after, extraDialog.sim.currency)}
                </div>
                <div className="text-[10px] text-[#8A9992] tabular-nums">
                  {extraDialog.mode === "reduce_term"
                    ? `Antes ${extraDialog.sim.months_remaining_before} · Ahora ${extraDialog.sim.months_remaining_after}`
                    : `Antes ${formatCurrency(extraDialog.sim.monthly_before, extraDialog.sim.currency)}`}
                </div>
              </div>
              {extraDialog.sim.interest_saved_estimate > 0 && (
                <div className="col-span-2 text-xs text-[#5F7368]">
                  Ahorro estimado en intereses: <b className="text-[#1C7C54] tabular-nums">
                    {formatCurrency(extraDialog.sim.interest_saved_estimate, extraDialog.sim.currency)}
                  </b>
                </div>
              )}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="extra-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="extra-confirm"
              onClick={confirmExtraAmort}
              disabled={!extraDialog?.mode}
              className="bg-[#0F291E] hover:bg-[#1C7C54]"
            >
              Marcar como extra
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

function MiniStat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-[#EBEBEA] px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992] flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`font-display text-lg font-semibold tabular-nums mt-1 ${tone || "text-[#0A110E]"}`}>{value}</div>
    </div>
  );
}
